import { $framework } from "@opendash/core";
import { makeAutoObservable, runInAction } from "mobx";
import React from "react";
import { KanbanGrouping, KanbanViewType } from "..";
import { TicketManagerConfig } from "../../fetching";
import { ProjectView } from "../components/views/ProjectView";
import { RoleView } from "../components/views/RoleView";
import { StateView } from "../components/views/StateView";
import { UserView } from "../components/views/UserView";

type HideEmptyColumns = {
  [key in KanbanGrouping]: boolean;
};

const HIDE_EMPTY_COLUMNS: HideEmptyColumns = {
  state: false,
  project: false,
  user: true,
  role: true,
};

type KanbanManagerConfig = {
  defaultView: KanbanGrouping;
  viewtype: KanbanViewType;
  lskey?: string;
  defaultTicketManagerConfig?: TicketManagerConfig;
};

/**
 * Manages the state of the Kanban board, including the current view, whether to show empty columns,
 * and whether to show the list view.
 * @class KanbanManager
 * @property {number} shouldReload - A counter to trigger a reload of the Kanban board.
 * @property {Record<string, number>} shouldReloadPerColumn - A counter to trigger a reload for each column.
 * @property {KanbanGrouping} _grouping - The current view type of the Kanban board.
 * @property {KanbanViewType} _viewtype - A flag to indicate if the Kanban board is in list view.
 * @property {boolean} _hideEmptyColumns - A flag to indicate if empty columns should be hidden.
 * @property {string} lskey - A key for local storage to persist the state values. If none is provided, local storage is not used.
 * @constructor
 * @param {KanbanGrouping} defaultView - The default view type of the Kanban board.
 * @param {KanbanViewType} viewtype - A flag to indicate if the Kanban board should be in list view.
 * @param {string} [lskey] - An optional key for local storage to persist the state values. If none is provided, local storage is not used.
 */
export class KanbanManager {
  private shouldReload: number = 0;
  private shouldReloadPerColumn: Record<string, number> = {};
  private _grouping: KanbanGrouping;
  private _viewtype: KanbanViewType;
  private _hideemptycolumns: boolean = false;
  private lskey: string = null;
  private config: KanbanManagerConfig = undefined;

  constructor(
    config: KanbanManagerConfig = {
      defaultView: "state",
      viewtype: "list",
      lskey: null,
      defaultTicketManagerConfig: undefined,
    }
  ) {
    this.config = config;
    this.lskey = config.lskey;
    this._grouping = config.defaultView;
    this._viewtype = config.viewtype;
    this._hideemptycolumns = HIDE_EMPTY_COLUMNS[config.defaultView];

    try {
      if (this.lskey !== null) {
        const viewtype =
          $framework.services.DeviceStorageService._getSync<KanbanViewType>(
            this.lskey + "-viewtype"
          );
        if (viewtype !== null && viewtype !== undefined) {
          this._viewtype = viewtype;
        }
        const hideEmpty =
          $framework.services.DeviceStorageService._getSync<boolean>(
            this.lskey + "-hideemptycolumns"
          );
        if (hideEmpty !== null && hideEmpty !== undefined) {
          this._hideemptycolumns = hideEmpty;
        }
        const grouping =
          $framework.services.DeviceStorageService._getSync<KanbanGrouping>(
            this.lskey + "-grouping"
          );
        if (grouping !== null && grouping !== undefined) {
          this._grouping = grouping;
        }
      }
    } catch (e) {}

    makeAutoObservable(this);
  }

  get hideEmpty() {
    return this._hideemptycolumns;
  }

  get viewType() {
    return this._viewtype;
  }

  get grouping() {
    return this._grouping;
  }

  getReload(id?: string) {
    if (!id) return this.shouldReload;
    return (this.shouldReloadPerColumn?.[id] ?? 0) + this.shouldReload;
  }

  setHideEmptyColumns(value: boolean) {
    runInAction(() => {
      if (this.lskey !== null) {
        $framework.services.DeviceStorageService.set(
          this.lskey + "-hideemptycolumns",
          value
        );
      }
      this._hideemptycolumns = value;
    });
  }

  setGrouping(view: KanbanGrouping) {
    runInAction(() => {
      if (this.lskey !== null) {
        $framework.services.DeviceStorageService.set(
          this.lskey + "-grouping",
          view
        );
      }
      this._grouping = view;
      // this._hideemptycolumns = HIDE_EMPTY_COLUMNS[view];
    });
  }

  setViewType(value: KanbanViewType) {
    runInAction(() => {
      if (this.lskey !== null) {
        $framework.services.DeviceStorageService.set(
          this.lskey + "-viewtype",
          value
        );
      }
      this._viewtype = value;
    });
  }

  setDefaultTicketManagerConfig(config: TicketManagerConfig | undefined) {
    runInAction(() => {
      this.config = {
        ...this.config,
        defaultTicketManagerConfig: config,
      };
    });
    this.reload();
  }

  reload(id?: string) {
    runInAction(() => {
      if (!id) {
        this.shouldReload += 1;
      } else {
        this.shouldReloadPerColumn[id] =
          (this.shouldReloadPerColumn?.[id] ?? 0) + 1;
      }
    });
  }

  getViewComponent() {
    switch (this._grouping) {
      case "state":
        return <StateView />;
      case "user":
        return <UserView />;
      case "role":
        return <RoleView />;
      case "project":
        return <ProjectView />;
    }
  }

  getDefaultTicketFilterConfig(): TicketManagerConfig {
    return this.config.defaultTicketManagerConfig;
  }
}
